/**
 * AuthContext — holds the signed-in user's Cognito tokens and exposes
 * sign-in / sign-out to the app.
 *
 * Tokens are kept in localStorage so a refresh or new tab stays signed in.
 * The ID token is refreshed shortly before it expires using the refresh token.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { config } from "../config";
import { refreshSession, revokeRefreshToken, type AuthTokens } from "./cognito";

const KEYS = {
  idToken: "xsc.auth.idToken",
  accessToken: "xsc.auth.accessToken",
  refreshToken: "xsc.auth.refreshToken",
  expiresAt: "xsc.auth.expiresAt",
};

// Refresh this long before expiry so API calls never race an expired token
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export interface AuthUser {
  email: string;
  /** UUID of the user's company (Cognito custom:companyId, set at sign-up). */
  companyId: string | null;
  /** From SSO (Google), when signed in that way: full name, first name, avatar URL. */
  name?: string;
  givenName?: string;
  picture?: string;
  /** How they signed in: "Google" (etc.) for SSO, "email" for a xenscope Commercial email & password account. */
  provider: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Current ID token for API calls (null when signed out). */
  idToken: string | null;
  /** Adopt tokens from a password sign-in or an SSO callback. */
  setSession: (tokens: AuthTokens) => void;
  signOut: () => void;
  /** Sign out completely: revoke, clear all app storage, end the Cognito SSO session. */
  signOutEverywhere: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** "Google" etc. from the ID token's identities claim (SSO), else "email". */
function providerOf(payload: Record<string, unknown>): string {
  try {
    const ids = typeof payload.identities === "string" ? JSON.parse(payload.identities) : payload.identities;
    const name = Array.isArray(ids) && ids[0]?.providerName;
    return typeof name === "string" && name ? name : "email";
  } catch {
    return "email";
  }
}

/** Read the user's claims from the ID token without verifying it (the API verifies). */
function userFromIdToken(idToken: string): AuthUser {
  try {
    const payload = JSON.parse(atob(idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return {
      email: payload.email ?? "",
      companyId: payload["custom:companyId"] ?? null,
      name: payload.name || undefined,
      givenName: payload.given_name || undefined,
      // Only https avatar URLs (Google's are lh3.googleusercontent.com)
      picture: typeof payload.picture === "string" && payload.picture.startsWith("https://") ? payload.picture : undefined,
      // SSO users carry an "identities" claim naming the provider; everyone else signed up with email
      provider: providerOf(payload),
    };
  } catch {
    return { email: "", companyId: null, provider: "email" };
  }
}

// localStorage can throw in privacy modes; treat that as "no session"
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persist(tokens: AuthTokens) {
  try {
    localStorage.setItem(KEYS.idToken, tokens.idToken);
    localStorage.setItem(KEYS.accessToken, tokens.accessToken);
    // Refreshes don't return a new refresh token, so keep the existing one
    if (tokens.refreshToken) localStorage.setItem(KEYS.refreshToken, tokens.refreshToken);
    localStorage.setItem(KEYS.expiresAt, String(Date.now() + tokens.expiresIn * 1000));
  } catch {
    // Session still works for this page load; it just won't survive a reload
  }
}

/**
 * Remove everything the app keeps in this browser: every xsc.* localStorage key
 * (tokens, expiry, and anything else of ours) and this site's sessionStorage
 * (the SSO verifier/state/return path). The theme choice (xsc-console-theme) is a
 * display preference, not account data, so it stays.
 */
function clear() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("xsc."))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // storage blocked: nothing to clear
  }
  try {
    sessionStorage.clear();
  } catch {
    // storage blocked: nothing to clear
  }
}

/** Revoke the refresh token at Cognito (best effort: the local clear happens regardless). */
function revoke() {
  const refreshToken = read(KEYS.refreshToken);
  if (refreshToken) void revokeRefreshToken(refreshToken).catch(() => undefined);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initial state comes from storage, discarding an already-expired token
  const [idToken, setIdToken] = useState<string | null>(() => {
    const token = read(KEYS.idToken);
    const expiresAt = Number(read(KEYS.expiresAt) ?? 0);
    return token && expiresAt > Date.now() ? token : null;
  });

  const setSession = useCallback((tokens: AuthTokens) => {
    persist(tokens);
    setIdToken(tokens.idToken);
  }, []);

  // Local sign-out (also used when a session can't be refreshed): revoke + clear
  const signOut = useCallback(() => {
    revoke();
    clear();
    setIdToken(null);
  }, []);

  // Full sign-out from the Sign out buttons: the above, then end the Cognito
  // Hosted UI session too (its cookie on auth.<domain>), so the next SSO sign-in
  // starts fresh; Cognito sends the browser back to /login
  const signOutEverywhere = useCallback(() => {
    revoke();
    clear();
    setIdToken(null);
    if (config.cognito.domain && config.cognito.clientId) {
      const params = new URLSearchParams({ client_id: config.cognito.clientId, logout_uri: `${window.location.origin}/login` });
      window.location.assign(`https://${config.cognito.domain}/logout?${params}`);
    }
  }, []);

  // Schedule a silent refresh shortly before the current token expires
  useEffect(() => {
    if (!idToken) return;
    const refreshToken = read(KEYS.refreshToken);
    if (!refreshToken) return;
    const expiresAt = Number(read(KEYS.expiresAt) ?? 0);
    const delay = Math.max(0, expiresAt - Date.now() - REFRESH_MARGIN_MS);
    const timer = window.setTimeout(() => {
      refreshSession(refreshToken).then(setSession).catch(signOut);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [idToken, setSession, signOut, signOutEverywhere]);

  const value = useMemo<AuthState>(() => {
    // Local-dev bypass: pretend to be signed in (never active in built bundles)
    if (config.authBypass) {
      return {
        user: { email: "dev@localhost", companyId: "00000000-0000-4000-8000-000000000000", provider: "email" },
        isAuthenticated: true,
        idToken: null,
        setSession,
        signOut,
        signOutEverywhere,
      };
    }
    return {
      user: idToken ? userFromIdToken(idToken) : null,
      isAuthenticated: Boolean(idToken),
      idToken,
      setSession,
      signOut,
      signOutEverywhere,
    };
  }, [idToken, setSession, signOut, signOutEverywhere]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
